import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';

const SectionWrapper = styled.section`
  --industries-background-color: #f9f9fd;

  width: 100%;
  max-width: var(--landing-content-max);
  background: #ffffff;
  padding: var(--landing-section-top) var(--landing-page-gutter) var(--landing-section-bottom);
  margin: 0 auto;
  box-sizing: border-box;
  position: relative;
  overflow: visible;
  
  @media (max-width: 480px) {
    padding: var(--landing-section-top) var(--landing-page-gutter) var(--landing-section-bottom);
  }
`;

const Container = styled.div`
  position: relative;
  margin: 0 auto;
  width: 100%;
  max-width: none;
  padding: 48px;
  padding-right: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 80px;
  background: var(--industries-background-color);
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: visible;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 40px;
    padding-right: 24px;
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }

  @media (max-width: 768px) {
    margin: 0 auto;
    padding: 32px 16px;
    gap: 32px;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    margin: 0 auto;
    padding: 24px 12px;
    gap: 24px;
    border-radius: 12px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  justify-content: center;
  max-width: 480px;
  width: 100%;
  flex: 0 0 480px;
  background: transparent;

  @media (max-width: 1024px) {
    align-items: center;
    text-align: center;
    max-width: 100%;
    flex: 1;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.56rem;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.17em;
  text-transform: uppercase;
  color: #8a909c;
  margin-bottom: 16px;

  &::before,
  &::after {
    content: "";
    display: block;
    width: 14px;
    height: 1px;
    background: currentColor;
  }
`;

const Title = styled.h2`
  font-size: 45px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px 0;
  line-height: 1.2;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  text-align: left;

  @media (max-width: 1024px) {
    text-align: center;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }

  @media (max-width: 480px) {
    font-size: 26px;
    margin: 0 0 16px 0;
  }
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`;

const DescriptionText = styled.p`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1rem;
  line-height: 1.6;
  color: #4b5563;
  margin: 0 0 24px 0;
  font-weight: 400;
  text-align: left;

  @media (max-width: 1024px) {
    text-align: center;
  }

  @media (max-width: 900px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin: 0 0 16px 0;
    line-height: 1.5;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 24px;
  width: 100%;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    margin-top: 20px;
    gap: 10px;
  }
`;

const GetStartedButton = styled.a`
  display: inline-flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  background-color: var(--smush-cta-primary);
  color: #ffffff !important;
  font-weight: 600;
  font-size: 15px;
  padding: 0 24px;
  height: 48px;
  min-width: 160px;
  border-radius: var(--smush-cta-radius);
  text-decoration: none;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: var(--smush-cta-primary-hover);
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 44px;
    font-size: 14px;
    min-width: auto;
  }
`;

const DiscussUseCaseButton = styled.a`
  display: inline-flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 24px;
  background: var(--smush-cta-secondary);
  color: var(--smush-cta-primary);
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  border-radius: var(--smush-cta-radius);
  border: 1px solid var(--smush-cta-secondary-border);
  transition: all 0.2s ease;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  white-space: nowrap;
  min-width: 160px;

  &:hover {
    transform: translateY(-2px);
    background: var(--smush-cta-secondary-hover);
    color: var(--smush-cta-primary);
    box-shadow: 0 8px 20px rgba(45, 54, 71, 0.1);
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 44px;
    font-size: 14px;
    min-width: auto;
  }
`;

const CarouselSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  min-width: 0;
  margin-left: -24px;
  margin-right: 0;
  padding-left: 24px;
  padding-right: 0;
  overflow: visible;
  background: transparent;

  @media (max-width: 1024px) {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
    align-items: center;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  max-width: none;
  padding-right: 0;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 150px;
    background: var(--industries-background-color);
    -webkit-mask-image: linear-gradient(to right,
      #000 0%,
      rgba(0, 0, 0, 0.9) 20%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.2) 80%,
      transparent 100%);
    mask-image: linear-gradient(to right,
      #000 0%,
      rgba(0, 0, 0, 0.9) 20%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.2) 80%,
      transparent 100%);
    pointer-events: none;
    z-index: 10;

    @media (max-width: 768px) {
      width: 60px;
    }

    @media (max-width: 480px) {
      width: 30px;
    }
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 150px;
    background: var(--industries-background-color);
    -webkit-mask-image: linear-gradient(to left,
      #000 0%,
      rgba(0, 0, 0, 0.9) 20%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.2) 80%,
      transparent 100%);
    mask-image: linear-gradient(to left,
      #000 0%,
      rgba(0, 0, 0, 0.9) 20%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.2) 80%,
      transparent 100%);
    pointer-events: none;
    z-index: 10;

    @media (max-width: 768px) {
      width: 60px;
    }

    @media (max-width: 480px) {
      width: 30px;
    }
  }
`;

const CarouselSlider = styled.div<{ translateX: number }>`
  display: flex;
  gap: 24px;
  transform: translate3d(${props => props.translateX}px, 0, 0);
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  width: fit-content;
  padding: 20px 0;
  padding-left: 0;

  @media (max-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const CarouselCard = styled.div<{ isActive?: boolean }>`
  min-width: 400px;
  max-width: 400px;
  background: #ffffff;
  border-radius: 35px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border: none;
  transition: all 0.3s ease;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: flex-start;
  border: 1px solid #e5e7eb;
  height: 600px;
  transform: ${props => props.isActive ? 'scale(1.02)' : 'scale(1)'};
  box-shadow: ${props => props.isActive
    ? '0 8px 24px rgba(221, 214, 254, 0.3)'
    : '0 2px 8px rgba(0, 0, 0, 0.05)'};
  border-color: ${props => props.isActive
    ? 'rgba(221, 214, 254, 0.6)'
    : '#e5e7eb'};

  &:hover {
    box-shadow: ${props => props.isActive
    ? '0 8px 24px rgba(221, 214, 254, 0.3)'
    : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  }

  @media (max-width: 768px) {
    min-width: 300px;
    max-width: 300px;
    height: 450px;
    padding: 20px;
    gap: 12px;
    border-radius: 24px;
  }

  @media (max-width: 480px) {
    min-width: 260px;
    max-width: 260px;
    height: 380px;
    padding: 16px;
    gap: 10px;
    border-radius: 20px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: #0b0f1a;
  margin: 0;
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  flex: 1;
  min-width: 0;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

// const Badge = styled.div<{ variant?: 'basic' | 'intermediate' }>`
//   display: inline-flex;
//   align-items: center;
//   padding: 4px 12px;
//   border-radius: 4px;
//   font-size: 12px;
//   font-weight: 600;
//   font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
//   background: ${props => props.variant === 'basic' ? '#107c10' : '#f7630c'};
//   color: #ffffff;
//   white-space: nowrap;
// `;

const CardDescription = styled.span`
  font-size: 14px;
  line-height: 1.5;
  color: #6b7280;
  margin: 0;
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  display: block;
  width: 100%;

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 1.4;
  }
`;

const TechBadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const TechBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 8px;
  background: #ffffff;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 500;
  color: #1a1a1a;
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  border: 1px solid #e5e7eb;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 2px 6px;
    gap: 4px;
  }
`;

const TechBadgeDot = styled.span`
  display: inline-flex;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(90deg, #ffb366 0%, #d8b4fe 100%);
  flex-shrink: 0;
`;

const ChatImageContainer = styled.div`
  position: absolute;
  bottom: -216px;
  right: -50px;
  height: 600px;
  opacity: 1;
  z-index: -2;
  pointer-events: none;
  overflow: visible;
  border: 8px solid #eef0f0;
  border-radius: 32px;
  box-shadow: 
    4px 4px 0 0 rgba(0, 0, 0, 0.2),
    8px 8px 0 0 rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    height: 400px;
    bottom: -150px;
    right: -30px;
    border-width: 5px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    height: 300px;
    bottom: -100px;
    right: -20px;
    border-width: 4px;
    border-radius: 16px;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: -30px;
    left: 50%;
    width: 85%;
    height: 60px;
    background: linear-gradient(to top, 
      rgba(0, 0, 0, 0.4) 0%,
      rgba(0, 0, 0, 0.2) 30%,
      rgba(0, 0, 0, 0.1) 60%,
      transparent 100%);
    border-radius: 50%;
    filter: blur(12px);
    z-index: -1;
    opacity: 0.7;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 26px;
    display: block;
    transition: transform 0.3s ease;
    backface-visibility: hidden;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 30%,
      transparent 70%,
      rgba(0, 0, 0, 0.1) 100%
    );
    border-radius: 16px;
    pointer-events: none;
    z-index: 1;
    mix-blend-mode: overlay;
  }
`;

const CardButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--smush-cta-secondary);
  z-index: 5;
  border: 1px solid var(--smush-cta-secondary-border);
  border-radius: var(--smush-cta-radius);
  color: var(--smush-cta-primary);
  text-decoration: none;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  transition: all 0.2s ease;
  cursor: pointer;
  margin-top: auto;

  &:hover {
    background: var(--smush-cta-secondary-hover);
    border-color: rgba(45, 54, 71, 0.34);
    box-shadow: 0 2px 8px rgba(45, 54, 71, 0.1);
  }
`;

const DesktopNavContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const MobileNavContainer = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
    margin-bottom: 8px;
    width: 100%;
  }
`;

const NavButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  color: #0b0f1a;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background: #ffffff;
    border-color: rgba(221, 214, 254, 0.4);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: scale(1.05);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
  }
`;

interface Industry {
  id: string;
  title: string;
  level: 'basic' | 'intermediate';
  description: string;
  features: Array<{ name: string; icon?: string }>;
  ctaUrl: string;
  ctaText: string;
}

const industries: Industry[] = [
  {
    id: '1',
    title: 'SaaS & Technology',
    level: 'intermediate',
    description: 'Guide users through product features, troubleshoot technical issues, assist with onboarding, and provide instant answers to frequently asked questions.',
    features: [
      { name: 'Feature tutorials' },
      { name: 'Technical troubleshooting' },
      { name: 'Onboarding assistance' },
      { name: 'API documentation' }
    ],
    ctaUrl: '/get-started',
    ctaText: 'Explore Use Case'
  },
  {
    id: '2',
    title: 'Education Platform',
    level: 'basic',
    description: 'Provide 24/7 academic support, answer course-related questions, guide students through enrollment processes, and offer personalized learning assistance.',
    features: [
      { name: 'Course guidance' },
      { name: 'Enrollment assistance' },
      { name: 'Resource recommendations' }
    ],
    ctaUrl: '/education',
    ctaText: 'Explore Use Case'
  },
  {
    id: '3',
    title: 'Finance & Healthcare',
    level: 'intermediate',
    description: 'Securely answer policy questions, explain services, schedule appointments, and provide compliant assistance while maintaining privacy and regulatory requirements.',
    features: [
      { name: 'Policy explanations' },
      { name: 'Appointment scheduling' },
    ],
    ctaUrl: '/get-started',
    ctaText: 'Explore Use Case'
  },
  {
    id: '4',
    title: 'E-commerce & Retail',
    level: 'basic',
    description: 'Help customers find products, answer shipping questions, process returns, and provide instant support for order inquiries with seamless WooCommerce integration.',
    features: [
      { name: 'Product recommendations' },
      { name: 'Order tracking' },
      { name: 'Lead generation forms' }
    ],
    ctaUrl: '/get-started',
    ctaText: 'Explore Use Case'
  }
];

const Industries: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const windowWidth = window.innerWidth;

        // Card widths and gaps based on breakpoints
        let cardWidthValue: number;
        let gap: number;

        if (windowWidth <= 480) {
          cardWidthValue = 260;
          gap = 12;
        } else if (windowWidth <= 768) {
          cardWidthValue = 300;
          gap = 16;
        } else {
          cardWidthValue = 400;
          gap = 24;
        }

        const cardWidthWithGap = cardWidthValue + gap;

        // Calculate the position to center the current card
        const centeredPosition = (containerWidth / 2) - (cardWidthValue / 2) - (currentIndex * cardWidthWithGap);
        setTranslateX(centeredPosition);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [currentIndex]);

  const handleNext = () => {
    if (currentIndex < industries.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const canGoNext = currentIndex < industries.length - 1;
  const canGoPrev = currentIndex > 0;

  return (
    <SectionWrapper id="use-cases">
      <Container>
        <HeaderSection>
          <Eyebrow>Versatile Solutions</Eyebrow>
          <Title>Built for every industry</Title>
          <Description>
            <DescriptionText>
              SmushChat is designed to revolutionize customer support across industries. Whether you're in education, SaaS, healthcare, or finance, our AI-powered assistant resolves up to 80% of customer queries through comprehensive documentation and seamless integrations.
            </DescriptionText>
            <DesktopNavContainer>
              <NavButton onClick={handlePrev} disabled={!canGoPrev} aria-label="Go to previous">
                <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.16 16.87a.5.5 0 1 0 .67-.74L3.67 10.5H17.5a.5.5 0 0 0 0-1H3.67l6.16-5.63a.5.5 0 0 0-.67-.74L2.24 9.44a.75.75 0 0 0 0 1.11l6.92 6.32Z" fill="currentColor" />
                </svg>
              </NavButton>
              <NavButton onClick={handleNext} disabled={!canGoNext} aria-label="Go to next">
                <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.84 3.13a.5.5 0 0 0-.68.74l6.17 5.63H2.5a.5.5 0 0 0 0 1h13.83l-6.17 5.63a.5.5 0 0 0 .68.74l6.91-6.32a.75.75 0 0 0 0-1.1l-6.91-6.32Z" fill="currentColor" />
                </svg>
              </NavButton>
            </DesktopNavContainer>
            <ButtonContainer>
              <GetStartedButton href="/get-started">
                Get Started
              </GetStartedButton>
              <DiscussUseCaseButton href="/get-started">
                Discuss your use case
              </DiscussUseCaseButton>
            </ButtonContainer>
          </Description>
        </HeaderSection>

        <CarouselSection>
          <CarouselContainer ref={containerRef}>
            <CarouselSlider translateX={translateX} ref={cardRef}>
              {industries.map((industry, index) => (
                <CarouselCard key={industry.id} isActive={index === currentIndex}>
                  <CardHeader>
                    <CardTitle>{industry.title}</CardTitle>
                    {/* <Badge variant={industry.level}>
                    {industry.level === 'basic' ? 'Popular' : 'Enterprise'}
                  </Badge> */}
                  </CardHeader>
                  <CardDescription as="span">{industry.description}</CardDescription>
                  <TechBadgesContainer>
                    {industry.features.map((feature, idx) => (
                      <TechBadge key={idx}>
                        <TechBadgeDot />
                        {feature.name}
                      </TechBadge>
                    ))}
                  </TechBadgesContainer>
                  {industry.id === '2' && (
                    <ChatImageContainer>
                      <img
                        src="/images/chat-interface-ellie.png"
                        alt="Ellie Chat Interface"
                        onError={(e) => {
                          // Fallback if image doesn't exist
                          const target = e.target as HTMLImageElement;
                          if (target) {
                            target.style.display = 'none';
                          }
                        }}
                      />
                    </ChatImageContainer>
                  )}
                  {industry.id === '3' && (
                    <ChatImageContainer>
                      <img
                        src="/images/chat-interface-warren.png"
                        alt="Warren AI Chat Interface"
                        onError={(e) => {
                          // Fallback if image doesn't exist
                          const target = e.target as HTMLImageElement;
                          if (target) {
                            target.style.display = 'none';
                          }
                        }}
                      />
                    </ChatImageContainer>
                  )}
                  <CardButton href={industry.ctaUrl}>
                    <span>{industry.ctaText}</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ marginLeft: '4px' }}>
                      <path d="M10.5 2.5a.5.5 0 0 1 .5.5v4h4a.5.5 0 0 1 0 1h-4v4a.5.5 0 0 1-1 0V8h-4a.5.5 0 0 1 0-1h4V3a.5.5 0 0 1 .5-.5z" />
                    </svg>
                  </CardButton>
                </CarouselCard>
              ))}
            </CarouselSlider>
          </CarouselContainer>
          <MobileNavContainer>
            <NavButton onClick={handlePrev} disabled={!canGoPrev} aria-label="Go to previous">
              <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.16 16.87a.5.5 0 1 0 .67-.74L3.67 10.5H17.5a.5.5 0 0 0 0-1H3.67l6.16-5.63a.5.5 0 0 0-.67-.74L2.24 9.44a.75.75 0 0 0 0 1.11l6.92 6.32Z" fill="currentColor" />
              </svg>
            </NavButton>
            <NavButton onClick={handleNext} disabled={!canGoNext} aria-label="Go to next">
              <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.84 3.13a.5.5 0 0 0-.68.74l6.17 5.63H2.5a.5.5 0 0 0 0 1h13.83l-6.17 5.63a.5.5 0 0 0 .68.74l6.91-6.32a.75.75 0 0 0 0-1.1l-6.91-6.32Z" fill="currentColor" />
              </svg>
            </NavButton>
          </MobileNavContainer>
        </CarouselSection>
      </Container>
    </SectionWrapper>
  );
};

export default Industries;
